import { graphql } from '@packages/gql/generated/shopping';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from 'urql';
import { useRecoElement } from '../../../helpers/useRecoElement';
import type { BrandsRecoSliderProps, RdeType, RequestParams } from '../../../types';
import { RecoBrandCard } from '../../RecoBrandCard';
import { useBrandsRecoTracking } from '../../RecoBrandCard/useBrandsRecoTracking';
import { RecoBrandCardSkeleton } from '../../RecoBrandCardSkeleton';
import { RecoCarousel } from '../../RecoCarousel';

export type PrudsysCategoryToBrandsProps = Pick<RequestParams, 'categoryId'> &
  Omit<BrandsRecoSliderProps, 'preferTitle'>;

const PrudsysCategoryToBrandsDocument = graphql(/* GraphQL */ `
  query PrudsysCategoryToBrands($categoryId: String!, $locale: String!) {
    prudsysCategoryToBrands(categoryId: $categoryId, locale: $locale) {
      recommendations {
        ...BrandRecommendationFragment
      }
    }
  }
`);

const RDE_TYPE: RdeType = 'prudsysCategoryToBrands';

/**
 * SimilarProductsRecoSlider component description displayed in storybook
 * */
export const PrudsysCategoryToBrandsRecoSlider: FC<PrudsysCategoryToBrandsProps> = ({
  categoryId,
  elementIndex = 0,
  fetchOnThreshold = false,
  pageTemplate,
  sourceCategoryId,
  title,
}) => {
  const { isInThreshold, isInView, recoEl } = useRecoElement();
  const { locale } = useIntl();

  const [{ data, error }] = useQuery({
    query: PrudsysCategoryToBrandsDocument,
    variables: {
      categoryId,
      locale: locale!,
    },
    context: useMemo(() => ({ suspense: false }), []),
    pause: fetchOnThreshold ? !isInThreshold : false,
  });

  const recommendations = data ? data.prudsysCategoryToBrands.recommendations : undefined;

  useBrandsRecoTracking({
    rdeType: RDE_TYPE,
    elementIndex,
    trackingCondition: isInView && !!recommendations && recommendations.length > 0,
    sourceCategoryId,
  });

  if ((error && !recommendations) || (recommendations && recommendations.length === 0)) {
    return null;
  }

  return (
    <RecoCarousel title={title} ref={recoEl} loadingState={!recommendations}>
      {!recommendations
        ? // rule is disabled because order doesn't change between rerenders
          // eslint-disable-next-line react/no-array-index-key
          [...Array(3)].map((_, i) => <RecoBrandCardSkeleton key={i} />)
        : recommendations.map((recommendation, idx) => (
            <RecoBrandCard
              pageTemplate={pageTemplate}
              // rule is disabled because order doesn't change between rerenders
              // eslint-disable-next-line react/no-array-index-key
              key={idx}
              position={idx}
              recommendationData={recommendation}
              brandsLength={recommendations.length}
              rdeType={RDE_TYPE}
            />
          ))}
    </RecoCarousel>
  );
};
